import React from 'react';

import IcCloseSm from '@deliveryhero/pd-cookbook/components/Icons/IcCloseSm';
import {
  Tag as BentoTag,
  TagVariant
} from '@deliveryhero/pd-cookbook/components/Tag';
import { Autocomplete as MuiAutocomplete } from '@mui/material';

import { Checkbox } from 'components/Checkbox';

import { AutocompleteOption, AutocompleteProps } from './types';

function Tag({
  enableChips,
  option,
  onDelete,
  ...rest
}: {
  enableChips: boolean;
  option: string;
  onDelete: React.EventHandler<React.SyntheticEvent>;
}) {
  return enableChips ? (
    <BentoTag
      id={TagVariant.INFORMATION}
      variant={TagVariant.INFORMATION}
      label={option}
      icon={
        <IcCloseSm
          fill="interaction-secondary"
          cursor="pointer"
          onClick={onDelete}
        />
      }
    />
  ) : (
    <span {...rest}>{option}</span>
  );
}

export function Autocomplete<T extends AutocompleteOption>({
  enableChips = false,
  limitTags,
  multiple = false,
  options,
  renderOption,
  value,
  ...props
}: AutocompleteProps<T>) {
  const handleGetOptionLabel = (option: T | string) =>
    typeof option == 'string' ? option : option.name;

  const checkOptionEqualToValue = (option: T, value: T) =>
    option.id == value.id;

  const { getOptionLabel, isOptionEqualToValue } = props;

  return multiple ? (
    <MuiAutocomplete
      {...props}
      multiple
      value={value as T[]}
      options={options}
      limitTags={enableChips ? limitTags : 1}
      defaultValue={[options[0]]}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel ? getOptionLabel : handleGetOptionLabel}
      isOptionEqualToValue={
        isOptionEqualToValue ? isOptionEqualToValue : checkOptionEqualToValue
      }
      renderOption={
        renderOption
          ? renderOption
          : (props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} label={option.name} />
              </li>
            )
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Tag
              key={key}
              enableChips={enableChips}
              option={option.name}
              {...rest}
            />
          );
        })
      }
    />
  ) : (
    <MuiAutocomplete
      {...props}
      value={value}
      options={options}
      getOptionLabel={getOptionLabel ? getOptionLabel : handleGetOptionLabel}
      isOptionEqualToValue={
        isOptionEqualToValue ? isOptionEqualToValue : checkOptionEqualToValue
      }
      renderOption={renderOption}
    />
  );
}
